import { computed, inject } from '@angular/core';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  EMPTY,
  exhaustMap,
  Observable,
  of,
  pipe,
  switchMap,
  tap
} from 'rxjs';
import {
  ActionState,
  ActionStateCreator,
  errorMessageParser,
  NotificationService
} from '@ui/legacy-lib';
import {
  advancePastSeen,
  createCombinedTooltipTour,
  createContinuedTooltipProgress,
  emptyTooltipProgress,
  initTooltipProgress
} from '@ui/tenant-pool/shared/tooltip/util';
import { TooltipFacade } from './tooltip.facade';
import type { Tooltip, TooltipProgress, TooltipTour } from '@ui/shared/model';

//** Keys carried over from the app's former `notificationConfig.tooltip`. */
const FETCH_TOOLTIPS_ERROR_KEY = 'tooltip.fetch_tooltips_fail_m';
const SET_TOOLTIPS_SEEN_ERROR_KEY = 'tooltip.set_tooltips_as_seen_fail_m';
const SET_TOOLTIP_TOUR_SEEN_ERROR_KEY =
  'tooltip.set_tooltip_tour_as_seen_fail_m';

export interface TooltipState {
  tooltips: Tooltip[];
  tooltipTours: TooltipTour[];
  pathTooltipsMap: Record<string, Tooltip[]>;
  pathTooltipToursMap: Record<string, TooltipTour[]>;
  tooltipsSeen: number[];
  tooltipProgress: TooltipProgress;
  tooltipsActionState: ActionState;
  tooltipToursActionState: ActionState;
  setTooltipsSeenActionState: ActionState;
}

export const initialState: TooltipState = {
  tooltips: [],
  tooltipTours: [],
  pathTooltipsMap: {},
  pathTooltipToursMap: {},
  tooltipsSeen: [],
  tooltipProgress: emptyTooltipProgress,
  tooltipsActionState: ActionStateCreator.create(),
  tooltipToursActionState: ActionStateCreator.create(),
  setTooltipsSeenActionState: ActionStateCreator.create()
};

export const TooltipStore = signalStore(
  { providedIn: 'root' },
  withState<TooltipState>(initialState),
  withDevtools('tooltip'),
  withComputed(({ tooltipsActionState }) => ({
    /**
     * True once the fetch has settled, error included: a failed fetch has to
     * release whoever is waiting. The classic slice only ever went true on
     * success, so a failing query stalled the first-time login check.
     */
    tooltipsLoaded: computed(() => {
      const actionState = tooltipsActionState();
      return !actionState.pending && (actionState.done || !!actionState.error);
    })
  })),
  withMethods(
    (
      store,
      facade = inject(TooltipFacade),
      notifications = inject(NotificationService)
    ) => ({
      loadTooltipsUnseen: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              tooltipsActionState: ActionStateCreator.onStart()
            })
          ),
          switchMap(path =>
            facade.tooltipsUnseen(path).pipe(
              tap(tooltips => {
                const pathTooltipsMap = {
                  ...store.pathTooltipsMap(),
                  [path]: tooltips
                };

                patchState(store, {
                  tooltips,
                  pathTooltipsMap,
                  tooltipProgress: initTooltipProgress(
                    createCombinedTooltipTour(
                      pathTooltipsMap,
                      store.pathTooltipToursMap(),
                      path
                    ),
                    store.tooltipsSeen()
                  ),
                  tooltipsActionState: ActionStateCreator.onSuccess()
                });
              }),
              catchError((e: Error) => {
                patchState(store, {
                  tooltipsActionState: ActionStateCreator.onError(e)
                });
                notifications.showErrorToast(
                  errorMessageParser(e, FETCH_TOOLTIPS_ERROR_KEY) as string
                );
                return EMPTY;
              })
            )
          )
        )
      ),

      loadTooltipToursUnseen: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              tooltipToursActionState: ActionStateCreator.onStart()
            })
          ),
          switchMap(path =>
            facade.tooltipToursUnseen(path).pipe(
              tap(tooltipTours => {
                const pathTooltipToursMap = {
                  ...store.pathTooltipToursMap(),
                  [path]: tooltipTours
                };

                patchState(store, {
                  tooltipTours,
                  pathTooltipToursMap,
                  tooltipProgress: initTooltipProgress(
                    createCombinedTooltipTour(
                      store.pathTooltipsMap(),
                      pathTooltipToursMap,
                      path
                    ),
                    store.tooltipsSeen()
                  ),
                  tooltipToursActionState: ActionStateCreator.onSuccess()
                });
              }),
              catchError((e: Error) => {
                patchState(store, {
                  tooltipToursActionState: ActionStateCreator.onError(e)
                });
                notifications.showErrorToast(
                  errorMessageParser(e, FETCH_TOOLTIPS_ERROR_KEY) as string
                );
                return EMPTY;
              })
            )
          )
        )
      ),

      markTooltipsSeen: rxMethod<number[]>(
        pipe(
          tap(() =>
            patchState(store, {
              setTooltipsSeenActionState: ActionStateCreator.onStart()
            })
          ),
          exhaustMap(ids =>
            facade.tooltipsSeen(ids).pipe(
              tap(() =>
                patchState(store, {
                  tooltipsSeen: [...store.tooltipsSeen(), ...ids],
                  setTooltipsSeenActionState: ActionStateCreator.onSuccess()
                })
              ),
              catchError((e: Error) => {
                // The ids are marked seen anyway so the tooltip stops
                // reappearing for this session, as the classic slice did.
                patchState(store, {
                  tooltipsSeen: [...store.tooltipsSeen(), ...ids],
                  setTooltipsSeenActionState: ActionStateCreator.onError(e)
                });
                notifications.showErrorToast(
                  errorMessageParser(e, SET_TOOLTIPS_SEEN_ERROR_KEY) as string
                );
                return EMPTY;
              })
            )
          )
        )
      ),

      markTourSeen: rxMethod<TooltipTour>(
        pipe(
          tap(() =>
            patchState(store, {
              setTooltipsSeenActionState: ActionStateCreator.onStart()
            })
          ),
          exhaustMap(tooltipTour => {
            const ids = tooltipTour.tourElements.map(
              tourElement => tourElement.tooltip.id
            );

            return facade.tooltipsSeen(ids).pipe(
              tap(() =>
                patchState(store, {
                  tooltipsSeen: [...store.tooltipsSeen(), ...ids],
                  setTooltipsSeenActionState: ActionStateCreator.onSuccess()
                })
              ),
              catchError((e: Error) => {
                patchState(store, {
                  tooltipsSeen: [...store.tooltipsSeen(), ...ids],
                  setTooltipsSeenActionState: ActionStateCreator.onError(e)
                });
                notifications.showErrorToast(
                  errorMessageParser(
                    e,
                    SET_TOOLTIP_TOUR_SEEN_ERROR_KEY
                  ) as string
                );
                return EMPTY;
              })
            );
          })
        )
      ),

      continueProgress(): void {
        const path =
          store.tooltipProgress().currentTourElement?.tooltip?.anchorPoint
            ?.path;
        const tours = path ? (store.pathTooltipToursMap()[path] ?? []) : [];

        patchState(store, {
          tooltipProgress: advancePastSeen(
            createContinuedTooltipProgress(store.tooltipProgress(), tours),
            tours,
            store.tooltipsSeen()
          )
        });
      },

      globalTooltips(): Observable<Tooltip[]> {
        return facade
          .tooltipsUnseen('global')
          .pipe(catchError(() => of([] as Tooltip[])));
      },

      resetProgress(): void {
        patchState(store, { tooltipProgress: emptyTooltipProgress });
      }
    })
  )
);
